import { EventItem, MySchedule } from "@/models/event-item";
import { Venue } from "@/models/venue";
import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  orderBy
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { Collections } from "@/helpers/utils/collections";

export const eventsMap = (doc: DocumentSnapshot): EventItem =>
({
  id: doc.id,
  ...doc.data(),
} as EventItem);

const myScheduleMap = (doc: QueryDocumentSnapshot): MySchedule =>
({
  id: doc.id,
  timestamp: doc.data().timestamp.toDate(),
} as MySchedule);

const venueMap = (doc: DocumentSnapshot): Venue => {
  const data = doc.data() as Venue;
  if (!data.imagePath) {
    const path = data.image.split("/").pop();
    data.imagePath = decodeURIComponent(
      path?.substring(0, path.indexOf("?")) ?? ""
    );
  }
  data.id = doc.id;
  return data;
};

export async function getEventSchedule(): Promise<EventItem[]> {
  const collectionRef = collection(db, Collections.EVENTS);
  const passSnapshot = await getDocs(collectionRef);
  const passData = passSnapshot.docs.map(eventsMap);
  return passData;
}

export async function getEventByDocumentId(
  documentId: string
): Promise<EventItem | undefined> {
  const passSnapshot = await getDoc(doc(db, Collections.EVENTS, documentId));
  return passSnapshot.exists() ? eventsMap(passSnapshot) : undefined;
}

// add to my schedule
export async function addMyScheduleToDB(
  eventId: string,
  uid: string
): Promise<void> {
  const myScheduleRef = doc(
    db,
    Collections.USERS,
    uid,
    Collections.MY_SCHEDULES,
    eventId
  );
  await setDoc(myScheduleRef, { eventId, timestamp: serverTimestamp() });
}

// remove from my schedule
export async function removeMyScheduleFromDB(
  eventId: string,
  uid: string
): Promise<void> {
  const myScheduleRef = doc(
    db,
    Collections.USERS,
    uid,
    Collections.MY_SCHEDULES,
    eventId
  );
  await deleteDoc(myScheduleRef);
}

// get my_schedule
export async function getMySchedule(uid: string): Promise<MySchedule[]> {
  // cut off date should be 1 year from now
  const cutOffDate = new Date();
  cutOffDate.setFullYear(cutOffDate.getFullYear() - 1);
  const myScheduleRef = collection(
    db,
    Collections.USERS,
    uid,
    Collections.MY_SCHEDULES
  );
  const myScheduleQuery = query(
    myScheduleRef,
    where("timestamp", ">", cutOffDate)
  );
  const myScheduleSnapshot = await getDocs(myScheduleQuery);
  const myScheduleData = myScheduleSnapshot.docs.map(myScheduleMap);
  return myScheduleData;
}

export async function getEventsForAdmin() {
  const collectionRef = collection(db, Collections.EVENTS);
  const currentYear = new Date().getFullYear();
  const startDate = new Date(currentYear, 0, 1);
  const endDate = new Date(currentYear, 11, 31);
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);
  const q = query(
    collectionRef,
    where("startTime", ">=", startTimestamp),
    where("startTime", "<=", endTimestamp)
  );
  const result = await getDocs(q);
  if (result.empty) {
    return [];
  }
  return result.docs.map(eventsMap);
}

export async function getEventsByLocation(location: string) {
  const collectionRef = collection(db, Collections.EVENTS);
  const q = query(
    collectionRef,
    where("location", "==",location )
  );
  const result = await getDocs(q);
  if (result.empty) {
    return [];
  }
  return result.docs.map(eventsMap);
}

export async function updateEvent(event: EventItem) {
  try {
    const eventRef = doc(db, Collections.EVENTS, event.id);
    const { id, ...rest } = event;
    await updateDoc(eventRef, { ...rest, lastModified: serverTimestamp() });
    const updatedEvent = await getDoc(eventRef);
    return eventsMap(updatedEvent);
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function addEvent(event: EventItem){
  try {
    const eventRef = collection(db, Collections.EVENTS);
    const { id, ...rest } = event;
    const addedRef = await addDoc(eventRef, { ...rest, lastModified: serverTimestamp() });
    const addedEvent = await getDoc(addedRef);
    return eventsMap(addedEvent);
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function getVenues(): Promise<Venue[]> {
  const categorySnapshot = await getDocs(
    query(collection(db, Collections.VENUE), orderBy("timestamp", "desc"))
  );
  let data = categorySnapshot.docs.map(venueMap);
  return data as Venue[];
}

export async function addUpdateVenue(venue: Venue) {
  try {
    return !venue.id
      ? await addVenue(venue)
      : await updateVenue(venue);
  } catch (e) {
    return undefined;
  }
}

async function updateVenue(venue: Venue) {
  const eventRef = doc(db, Collections.VENUE, venue.id ?? "");
  const { id, ...rest } = venue;
  await updateDoc(eventRef, { ...rest });
  const updatedVenue = await getDoc(eventRef);
  return venueMap(updatedVenue);
}

async function addVenue(venue: Venue) {
  const eventRef = collection(db, Collections.VENUE);
  const { id, ...rest } = venue;
  const response = await addDoc(eventRef, { ...rest });
  const addedVenue = await getDoc(response);
  return venueMap(addedVenue);
}
